import React, { useState, useEffect } from 'react';
import { 
  FaCheckCircle, FaStar, FaSearch, FaRocket, FaShieldAlt, 
  FaChartLine, FaQuoteLeft, FaRobot, FaBriefcase, FaUserShield 
} from 'react-icons/fa';
import './ProposedBusinessLanding.css';

const ProposedBusinessLanding = () => {
  const [activeJob, setActiveJob] = useState(0);
  const [showToast, setShowToast] = useState(false);
  const [toastContent, setToastContent] = useState('');

  // Mock Social Proof Engine Activities [8]
  const activities = [
    "A homeowner in Sydney just posted a job for a Plumber.",
    "Someone in Melbourne is searching for an Electrician.",
    "We just connected a family in Perth with 'Pro Painters WA'.",
    "A builder in Brisbane just posted a job for a Concreter."
  ];

  useEffect(() => {
    const toastInterval = setInterval(() => {
      setToastContent(activities[Math.floor(Math.random() * activities.length)]);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 5000);
    }, 10000);
    return () => clearInterval(toastInterval);
  }, []);

  return (
    <div className="business-landing-page">
      {/* Social Proof Toast [9, 10] */}
      <div className={`activity-toast ${showToast ? 'visible' : ''}`}>
        <FaRocket className="toast-icon" />
        <span>{toastContent}</span>
      </div>

      {/* Navbar [11, 12] */}
      <nav className="navbar">
        <div className="nav-container">
          <div className="logo-gold">BUILDSTART</div>
          <div className="nav-links">
            <a href="#home">Home Owner</a>
            <a href="#pro" className="active">For Building Professionals</a>
            <a href="#dev">For Renovators / Developers</a>
            <a href="#hub">Resource Hub</a>
            <a href="#support">Support</a>
          </div>
          <div className="nav-auth">
            <button className="btn-login-outline">Login</button>
            <button className="btn-register-filled">Register</button>
          </div>
        </div>
      </nav>

      {/* Hero Section [1, 13] */}
      <header className="hero-section">
        <div className="container">
          <div className="rating-badge">
            <div className="stars">★★★★★</div>
            <span>4.9 Rated, 200+ Reviews</span>
          </div>
          <h1 className="hero-title-gold">LET QUALITY LEADS <br/> COME TO YOU</h1>
          <p className="hero-subtitle">
            Build Start is an invitation-only directory of the most trusted professionals. 
            We connect you with serious homeowners ready to hire. [14]
          </p>
          <div className="hero-search">
            <input type="text" placeholder="E.g. Plumber, Electrician, Surveyor" />
            <button className="btn-search"><FaSearch /> Search</button>
          </div>
          <div className="hero-ctas">
            <button className="btn-primary">Post a Job</button>
            <button className="btn-secondary">Connect using our Social Network</button>
          </div>
        </div>
      </header>

      {/* Verified Pro Badge Section [3, 15, 16] */}
      <section className="verified-section">
        <div className="container grid-2">
          <div className="verified-badge-large">
            <div className="shield-icon">
              <FaShieldAlt />
              <FaCheckCircle className="check-sub" />
            </div>
            <h3>VERIFIED PRO</h3>
          </div>
          <div className="verified-text">
            <h2>DISTINGUISH YOURSELF FROM THE "SCAMMERS" [17]</h2>
            <p>
              Obtaining the <strong>"Verified Pro" badge</strong> signals to every lead that you have 
              passed rigorous documentation and licensing checks. This specialized status helps you 
              win more work and attract premium users. [3, 17]
            </p>
          </div>
        </div>
      </section>

      {/* AI Power Section [1, 18, 19] */}
      <section className="ai-section">
        <div className="container grid-2">
          <div className="ai-content">
            <h2>LEVERAGE THE POWER OF <span className="ai-pill">AI <FaRobot /></span></h2>
            <p>
              Experience the industry's first AI-powered platform to promote your business. 
              Our <strong>AI Virtual Assistant</strong> ensures you receive highly detailed leads, 
              saving you time on chasing missing details. [1, 20, 21]
            </p>
          </div>
          <div className="ai-visual">
            <div className="chat-bubble-mock">"Build a deck in my backyard"</div>
            <div className="ai-response-mock">AI Suggests: Deck Builder, Carpenter... [18]</div>
          </div>
        </div>
      </section>

      {/* Live Job Feed [7, 22] */}
      <section className="job-feed-section">
        <div className="container">
          <h2 className="section-title">AVAILABLE JOBS IN YOUR AREA</h2>
          <p className="section-subtitle">Someone just posted these jobs. Don't miss out! [1]</p>
          <div className="job-grid">
            <div className="job-card">
              <span className="job-icon">⚡</span>
              <h4>Rewiring Job</h4>
              <p>Upgrade switchboard and run new cables. [7]</p>
              <div className="job-meta">Budget: $2500 - $5000</div>
              <button className="btn-view">View Details</button>
            </div>
            <div className="job-card">
              <span className="job-icon">📐</span>
              <h4>Architectural Plans</h4>
              <p>Detailed plans for a 2-story extension.</p>
              <div className="job-meta">Budget: $5000+</div>
              <button className="btn-view">View Details</button>
            </div>
            <div className="job-card highlight-urgent">
              <span className="job-icon">❄️</span>
              <h4>AC Installation <span className="urgent-tag">URGENT</span></h4>
              <p>Installation of 3 split system units.</p>
              <div className="job-meta">Budget: $1000 - $2500</div>
              <button className="btn-view">View Details</button>
            </div>
          </div>
        </div>
      </section>

      {/* The 4-Point Gauntlet [23, 24] */}
      <section className="gauntlet-section">
        <div className="container">
          <h2 className="section-title">THE BUILDSTART SEAL OF APPROVAL</h2>
          <div className="gauntlet-grid">
            <div className="gauntlet-step">
              <div className="step-num">1</div>
              <h4>License & Insurance</h4>
              <p>We verify every license and insurance policy to ensure they are current. [23]</p>
            </div>
            <div className="gauntlet-step">
              <div className="step-num">2</div>
              <h4>Reputation Analysis</h4>
              <p>Meticulous analysis of online reviews with a 3-star minimum requirement. [25]</p>
            </div>
            <div className="gauntlet-step">
              <div className="step-num">3</div>
              <h4>Portfolio Check</h4>
              <p>We review past work for quality and consistency in craftsmanship. [25]</p>
            </div>
            <div className="gauntlet-step">
              <div className="step-num">4</div>
              <h4>Continuous Monitoring</h4>
              <p>Ongoing performance tracking to ensure standards are always met. [25]</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Tiers [26, 27] */}
      <section className="pricing-section">
        <div className="container">
          <h2 className="section-title">PLANS DESIGNED FOR GROWTH</h2>
          <div className="pricing-grid">
            <div className="price-card">
              <h3>Starter Pack</h3>
              <div className="price">$99<span>/mo</span></div>
              <ul>
                <li><FaCheckCircle /> Instant Searchable Listing</li>
                <li><FaCheckCircle /> Unlimited Job Leads</li>
                <li><FaCheckCircle /> "Verified Pro" Badge</li>
              </ul>
              <button className="btn-price">Start Free Trial</button>
            </div>
            <div className="price-card popular">
              <div className="popular-tag">Most Popular</div>
              <h3>Pro Pack</h3>
              <div className="price">$140<span>/mo</span></div>
              <ul>
                <li><FaCheckCircle /> Priority Leads (1hr early)</li>
                <li><FaCheckCircle /> 4x Sponsored Social Posts</li>
                <li><FaCheckCircle /> Social Video Posts</li>
              </ul>
              <button className="btn-price-gold">Start Free Trial</button>
            </div>
            <div className="price-card">
              <h3>Corporate Pack</h3>
              <div className="price">$999<span>/mo</span></div>
              <ul>
                <li><FaCheckCircle /> Featured on Home Page</li>
                <li><FaCheckCircle /> Top of Search Results</li>
                <li><FaCheckCircle /> Dedicated Account Manager</li>
              </ul>
              <button className="btn-price">Start Free Trial</button>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison "FOMO Generator" [27, 28] */}
      <section className="comparison-section">
        <div className="container">
          <h2 className="section-title">COMPARE FEATURES</h2>
          <table className="comparison-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th>Basic (Free)</th>
                <th>Starter / Pro</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Directory Listing</td>
                <td className="red-x">Bottom of List</td>
                <td className="green-check">High Visibility</td>
              </tr>
              <tr>
                <td>Job Leads</td>
                <td className="red-x">✘ None</td>
                <td className="green-check">✔ Unlimited</td>
              </tr>
              <tr>
                <td>Contact Details</td>
                <td className="red-x">✘ Locked</td>
                <td className="green-check">✔ Unlocked</td>
              </tr>
              <tr>
                <td>Verified Badge</td>
                <td className="red-x">✘ No</td>
                <td className="green-check">✔ Yes</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Footer [29-31] */}
      <footer className="footer-dark">
        <div className="container footer-grid">
          <div className="footer-brand">
            <h2 className="logo-gold">BUILDSTART</h2>
            <p>Building the Future of Renovation. [32]</p>
          </div>
          <div className="footer-links">
            <h4>Quick Links</h4>
            <ul>
              <li>For Home Owners</li>
              <li>For Professionals</li>
              <li>Resource Hub</li>
            </ul>
          </div>
          <div className="footer-links">
            <h4>Directory</h4>
            <ul>
              <li>Electricians</li>
              <li>Plumbers</li>
              <li>Builders</li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© 2026 Build Start. All Rights Reserved. [33]</p>
        </div>
      </footer>
    </div>
  );
};

export default ProposedBusinessLanding;